import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDate, Badge, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/StatCard";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary } from "../components/Modal";
import { toast } from "sonner";
import { HandCoins, Coins } from "lucide-react";

export default function Debts() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [data, setData] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ method: "Espèces", amount: "" });

  const load = () => api.get("/debts").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const doPay = async () => {
    try {
      await api.post(`/purchases/${payFor.id}/payments`, { method: pay.method, amount: Number(pay.amount) });
      toast.success("Règlement fournisseur enregistré");
      setPayFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="py-20 text-center text-muted-foreground">Chargement...</div>;

  const supCols = [
    { key: "name", label: "Fournisseur", render: (s) => <span className="font-semibold">{s.name}</span> },
    { key: "phone", label: "Téléphone" },
    { key: "total_purchased", label: "Total acheté", className: "text-right", render: (s) => <span className="font-money">{fmtMoney(s.total_purchased, cur)}</span> },
    { key: "balance", label: "Dette", className: "text-right", render: (s) => <span className="font-money font-bold text-rose-600">{fmtMoney(s.balance, cur)}</span> },
  ];

  const purCols = [
    { key: "number", label: "N° Achat", render: (p) => <span className="font-money font-bold">{p.number}</span> },
    { key: "date", label: "Date", render: (p) => fmtDate(p.date) },
    { key: "supplier_name", label: "Fournisseur" },
    { key: "total", label: "Total", className: "text-right", render: (p) => <span className="font-money">{fmtMoney(p.total, cur)}</span> },
    { key: "balance", label: "Reste dû", className: "text-right", render: (p) => <span className="font-money font-bold text-rose-600">{fmtMoney(p.balance, cur)}</span> },
    { key: "payment_status", label: "Statut", render: (p) => <Badge status={p.payment_status === "unpaid" ? "unpaid" : p.payment_status} /> },
    ...(hasPerm("purchases.create") ? [{ key: "actions", label: "", className: "text-right", render: (p) => (
      <button data-testid={`pay-debt-${p.id}`} onClick={() => { setPayFor(p); setPay({ method: "Espèces", amount: String(p.balance) }); }}
        className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs font-bold">
        <Coins className="h-4 w-4" /> Régler</button>) }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="debts-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Dettes Fournisseurs</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard testid="debts-total" label="Total des dettes" value={fmtMoney(data.total, cur)} icon={HandCoins} tone="rose" />
        <StatCard testid="debts-suppliers" label="Fournisseurs créditeurs" value={data.suppliers.length} icon={HandCoins} tone="amber" />
        <StatCard testid="debts-purchases" label="Achats non soldés" value={data.open_purchases.length} icon={HandCoins} tone="blue" />
      </div>
      <h2 className="font-heading font-bold text-lg">Par fournisseur</h2>
      <DataTable testid="debts-suppliers-table" columns={supCols} rows={data.suppliers} searchKeys={["name", "phone"]} empty="Aucune dette" />
      <h2 className="font-heading font-bold text-lg">Achats non soldés</h2>
      <DataTable testid="debts-purchases-table" columns={purCols} rows={data.open_purchases} searchKeys={["number", "supplier_name"]} empty="Aucun achat ouvert" />

      <Modal open={!!payFor} onClose={() => setPayFor(null)} title={`Régler — ${payFor?.number}`}>
        <div className="space-y-3">
          <p className="text-sm">Reste dû : <b className="font-money">{fmtMoney(payFor?.balance, cur)}</b></p>
          <Field label="Mode">
            <select data-testid="debt-pay-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className={selectCls}>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Montant"><input data-testid="debt-pay-amount" type="number" min="0" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className={inputCls} /></Field>
          <button data-testid="debt-pay-confirm" onClick={doPay} disabled={!Number(pay.amount)} className={`${btnPrimary} w-full`}>Enregistrer</button>
        </div>
      </Modal>
    </div>
  );
}
